import { fetchAuthSession } from 'aws-amplify/auth'
import type { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios'
import Axios from 'axios'
import { jwtDecode } from 'jwt-decode'

import { env } from 'src/config/env'
import { logger } from 'src/lib/logger'
import { convertKeysToCamelCase } from 'src/shared/utils/case-conversion'

import type { AuthTokensType } from './token-provider'
import { getTokens, clearTokens, refreshTokens } from './token-provider'

const API_BASE_URL = `${env.BACKEND_URL}`

interface JwtPayload {
  exp?: number
  [key: string]: unknown
}

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JwtPayload>(token)
    if (!decoded.exp) return false
    // Check if token expires within the next 30 seconds (buffer for network latency)
    const expirationTime = decoded.exp * 1000
    return Date.now() >= expirationTime - 30000
  } catch {
    return true
  }
}

const fetchLocalTokens = async (): Promise<AuthTokensType | null> => {
  const tokens = getTokens()

  // return null

  if (!tokens) {
    return null
  }

  // Check if the access token is expired
  if (!isTokenExpired(tokens.accessToken)) {
    return tokens
  }

  const refreshed = await refreshTokens()

  if (!refreshed) {
    clearTokens()
    throw new Error('Failed to refresh session. Please log in again.')
  }

  // Get the new tokens
  const newTokens = getTokens()
  if (!newTokens) {
    return null
  }

  return newTokens
}

const getCurrentAccessToken = async (): Promise<string | undefined> => {
  try {
    // Check local storage for tokens.
    const tokens = await fetchLocalTokens()
    if (tokens) {
      return tokens.accessToken
    }
    // If there are no local tokens, try to get AWS Session Tokens.
    const session = await fetchAuthSession()
    const token = session.tokens?.accessToken.toString()
    return token
  } catch (error) {
    logger.error('Error fetching access token:', error)
    return undefined
  }
}

interface ReqError extends Error {
  response?: {
    data: {
      message?: string
      detail?: string
    }
  }
}

class RouteErrorResponse extends Error {
  constructor(public status: number, public statusText: string, public data: string) {
    super(statusText)
  }
}

const handleReqError = (error: ReqError, defaultMessage: string): never => {
  if (Axios.isAxiosError(error) && error.response) {
    const errorMessage = error.response.data.message || error.response.data.detail || defaultMessage
    const status = error.response.status || 500
    throw new RouteErrorResponse(status, errorMessage, errorMessage)
  }
  logger.error('Unexpected error:', error)
  throw new RouteErrorResponse(500, 'Internal Server Error', 'An unexpected error occurred')
}

const axiosClient = Axios.create({
  baseURL: API_BASE_URL,
})

axiosClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = await getCurrentAccessToken()

  config.headers.Authorization = `Bearer ${token}`
  config.headers['Content-Type'] = 'application/json'

  return config
})

axiosClient.interceptors.response.use(
  (response: AxiosResponse) => {
    response.data = convertKeysToCamelCase(response.data)
    return response
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  },
)

export const getRequest = async <T>(url: string, params?: object): Promise<T | undefined> => {
  try {
    const response = await axiosClient.get<T>(url, { params })
    return response.data
  } catch (error) {
    handleReqError(error as ReqError, `An error occurred while fetching data from ${url}`)
    return undefined
  }
}

export const postRequest = async <T>(url: string, data?: object | boolean): Promise<T | undefined> => {
  try {
    const response = await axiosClient.post<T>(url, data)
    return response.data
  } catch (error) {
    handleReqError(error as ReqError, `An error occurred while posting data to ${url}`)
    return undefined
  }
}

export const putRequest = async <T>(url: string, data?: object): Promise<T | undefined> => {
  try {
    const response = await axiosClient.put<T>(url, data)
    return response.data
  } catch (error) {
    handleReqError(error as ReqError, `An error occurred while updating data at ${url}`)
    return undefined
  }
}

export const patchRequest = async <T>(url: string, data?: object): Promise<T | undefined> => {
  try {
    const response = await axiosClient.patch<T>(url, data)
    return response.data
  } catch (error) {
    handleReqError(error as ReqError, `An error occurred while patching data at ${url}`)
    return undefined
  }
}

export const deleteRequest = async <T>(url: string): Promise<T | undefined> => {
  try {
    const response = await axiosClient.delete<T>(url)
    return response.data
  } catch (error) {
    handleReqError(error as ReqError, `An error occurred while deleting data at ${url}`)
    return undefined
  }
}
